import React from 'react';

const shimmerBox = (overrides: React.CSSProperties = {}): React.CSSProperties => ({
  background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 2s infinite',
  borderRadius: 8,
  ...overrides,
});

/**
 * Skeleton loader for PayrollManagement's "Salaries" tab — mirrors the
 * shimmer/pulse pattern from StudentsListSkeletonLoader.tsx (same keyframes,
 * same table-row rhythm) but shaped for the payroll table columns:
 * #, Employee, Payroll Role, System Role, Status, Salary, PF, Prof. Tax,
 * TDS %, Effective From, Actions.
 */
const PayrollManagementSkeletonLoader: React.FC = () => {
  return (
    <>
      <div
        style={{
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {/* Info bar skeleton (count + filters + add button) */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #f3f4f6',
            background: '#fafbfc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={shimmerBox({ width: 32, height: 32, borderRadius: 8 })} />
            <div style={shimmerBox({ width: 140, height: 16 })} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={shimmerBox({ width: 110, height: 32, borderRadius: 10 })} />
            <div style={shimmerBox({ width: 100, height: 32, borderRadius: 10 })} />
            <div style={shimmerBox({ width: 100, height: 32, borderRadius: 10 })} />
          </div>
        </div>

        {/* Search bar skeleton */}
        <div style={{ margin: '16px 20px 0' }}>
          <div style={shimmerBox({ width: '100%', height: 46, borderRadius: 16 })} />
        </div>

        {/* Table header skeleton */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '20px 20px 12px',
          }}
        >
          {[28, 180, 90, 90, 80, 110, 80, 80, 60, 100, 90].map((w, i) => (
            <div key={i} style={shimmerBox({ width: w, height: 11, flexShrink: 0 })} />
          ))}
        </div>

        {/* Table rows skeleton */}
        <div style={{ padding: '0 20px 20px' }}>
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '16px 0',
                borderBottom: index < 7 ? '1px solid #f3f4f6' : 'none',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${index * 0.08}s`,
              }}
            >
              {/* # */}
              <div style={shimmerBox({ width: 28, height: 28, borderRadius: 6, flexShrink: 0 })} />

              {/* Employee (avatar + name/id) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 180, flexShrink: 0 }}>
                <div style={shimmerBox({ width: 36, height: 36, borderRadius: 10, flexShrink: 0 })} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={shimmerBox({ width: '80%', height: 14, marginBottom: 6 })} />
                  <div style={shimmerBox({ width: '50%', height: 11 })} />
                </div>
              </div>

              {/* Payroll Role */}
              <div style={shimmerBox({ width: 80, height: 20, borderRadius: 20, flexShrink: 0 })} />

              {/* System Role */}
              <div style={shimmerBox({ width: 80, height: 20, borderRadius: 20, flexShrink: 0 })} />

              {/* Status */}
              <div style={shimmerBox({ width: 70, height: 20, borderRadius: 20, flexShrink: 0 })} />

              {/* Salary */}
              <div style={{ width: 110, flexShrink: 0 }}>
                <div style={shimmerBox({ width: '70%', height: 14, marginBottom: 6 })} />
                <div style={shimmerBox({ width: '90%', height: 11 })} />
              </div>

              {/* PF */}
              <div style={shimmerBox({ width: 70, height: 18, borderRadius: 6, flexShrink: 0 })} />

              {/* Prof. Tax */}
              <div style={shimmerBox({ width: 60, height: 14, flexShrink: 0 })} />

              {/* TDS % */}
              <div style={shimmerBox({ width: 40, height: 14, flexShrink: 0 })} />

              {/* Effective From */}
              <div style={shimmerBox({ width: 80, height: 14, flexShrink: 0 })} />

              {/* Actions */}
              <div style={shimmerBox({ width: 90, height: 28, borderRadius: 9, flexShrink: 0, marginLeft: 'auto' })} />
            </div>
          ))}
        </div>
      </div>

      {/* Shared shimmer/pulse animation styles (matches StudentsListSkeletonLoader) */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
};

export default PayrollManagementSkeletonLoader;
